import { useEffect, useRef } from 'react';
import { computeBeatState } from '../engine/beatSystem';
import { activeVisualEvent } from '../engine/timeline';
import { prepareTrack } from '../engine/trackPreparation';
import { usePartyStore } from '../store/partyStore';
import { VISUAL_STYLES, type VisualStyle } from '../types';

const DEMO_DURATION_MS = 480_000;
const DEMO_BPM = 128;
const DEMO_ALBUM_ART_URL = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 600">
  <defs>
    <linearGradient id="coverBg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#00d7ff"/>
      <stop offset="0.5" stop-color="#7a3cff"/>
      <stop offset="1" stop-color="#ffcf38"/>
    </linearGradient>
    <radialGradient id="glow" cx="50%" cy="45%" r="48%">
      <stop offset="0" stop-color="#ffffff" stop-opacity="0.8"/>
      <stop offset="1" stop-color="#ffffff" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="600" height="600" fill="url(#coverBg)"/>
  <circle cx="300" cy="278" r="260" fill="url(#glow)"/>
  <path d="M76 382 C166 248 243 470 338 310 C421 170 475 288 536 214" fill="none" stroke="#ffffff" stroke-width="26" stroke-linecap="round" opacity="0.82"/>
  <path d="M92 455 L508 455" stroke="#111827" stroke-width="18" stroke-linecap="round" opacity="0.55"/>
  <text x="300" y="516" text-anchor="middle" font-family="Arial, sans-serif" font-size="44" font-weight="800" fill="#10151f">PARTY MODE</text>
</svg>
`)}`;

export function usePartyDemoSync(enabled: boolean) {
  const rafRef = useRef<number | undefined>(undefined);
  const startRef = useRef(0);
  const offsetRef = useRef(0);
  const beatIndexRef = useRef(-1);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const params = new URLSearchParams(window.location.search);
    const requestedDurationMs = Number(params.get('demoDurationMs') ?? DEMO_DURATION_MS);
    const durationMs = Number.isFinite(requestedDurationMs)
      ? Math.max(DEMO_DURATION_MS, requestedDurationMs)
      : DEMO_DURATION_MS;
    const startMs = Number(params.get('startMs') ?? params.get('progressMs') ?? 0);
    offsetRef.current = Number.isFinite(startMs)
      ? Math.max(0, Math.min(durationMs - 1, startMs))
      : 0;
    startRef.current = performance.now();
    beatIndexRef.current = -1;

    const state = usePartyStore.getState();
    const requestedStyle = params.get('style') as VisualStyle | null;
    if (requestedStyle && VISUAL_STYLES.some((style) => style.id === requestedStyle)) {
      state.setVisualStyle(requestedStyle);
    }
    state.setSyncStatus('synced');
    state.setSpotifyDevice('Preview Clock', 'Demo');
    state.setLyricsStatus('synced');
    state.setNextTrack({
      id: 'demo-next',
      title: 'Queue Preview',
      artist: 'Prepared ahead',
      albumArtUrl: DEMO_ALBUM_ART_URL,
      durationMs: 188_000,
      prepared: true,
    });
    state.setQueueTracks([
      {
        id: 'demo-next',
        title: 'Queue Preview',
        artist: 'Prepared ahead',
        albumArtUrl: DEMO_ALBUM_ART_URL,
        durationMs: Math.min(durationMs, 188_000),
        prepared: true,
      },
      {
        id: 'demo-after',
        title: 'Late Night Build',
        artist: 'Rule Engine',
        albumArtUrl: DEMO_ALBUM_ART_URL,
        durationMs: 226_000,
        prepared: true,
      },
      {
        id: 'demo-outro',
        title: 'Outro Reform',
        artist: 'Album Art Cache',
        albumArtUrl: DEMO_ALBUM_ART_URL,
        durationMs: 194_000,
        prepared: false,
      },
    ]);
    state.markTrackPrepared('demo-next');
    state.markTrackPrepared('demo-after');

    prepareTrack(
      {
        id: 'party-demo-track',
        title: 'Deterministic Preview',
        artist: 'How You Met Me',
        album: 'Party Mode',
        durationMs,
        albumArtUrl: DEMO_ALBUM_ART_URL,
        bpm: DEMO_BPM,
      },
      state.visualStyle,
      { lazy: true }
    ).then((track) => {
      if (!alive) return;
      usePartyStore.getState().setCurrentTrack({
        ...track,
        hasLyrics: true,
        lyrics: [
          {
            startMs: 0,
            endMs: durationMs,
            text: 'Feel the drop',
            normalizedText: 'feel the drop',
            weight: 3,
            isRepeated: true,
            isLikelyHook: true,
          },
        ],
        hooks: [
          {
            startMs: 0,
            endMs: durationMs,
            text: 'Feel the drop',
            normalizedText: 'feel the drop',
            weight: 3,
            isRepeated: true,
            isLikelyHook: true,
          },
        ],
      });
      usePartyStore.getState().markTrackPrepared(track.spotifyTrackId);
    });

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const store = usePartyStore.getState();
      const progressMs = (offsetRef.current + performance.now() - startRef.current) % durationMs;
      const bpm = store.manualBpm ?? DEMO_BPM;
      const beat = computeBeatState(progressMs, { bpm, manualTapTimes: store.tapTimes }, beatIndexRef.current);
      if (beat.isBeat) beatIndexRef.current = beat.beatIndex;

      let activeEvent = store.currentTrack
        ? activeVisualEvent(store.currentTrack.visualTimeline, progressMs)
        : null;

      if (store.sceneOverride && progressMs < store.sceneOverride.endMs) {
        activeEvent = store.sceneOverride;
      }

      if (activeEvent && store.overrides.sceneLocked && store.overrides.forcedSceneId && activeEvent.sceneId !== store.overrides.forcedSceneId) {
        activeEvent = { ...activeEvent, sceneId: store.overrides.forcedSceneId };
      }

      store.setPlayback(progressMs, true, beat);
      store.setActiveEvent(activeEvent);
      store.setDisplayLyric(store.overrides.lyricsEnabled ? 'FEEL THE DROP' : '');
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      alive = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled]);
}
